import { useEffect } from 'react';
import { useOpenCall } from '@/features/calls/hooks/useOpenCall';
import { callPopupActions, callPopupStore } from '@/features/calls/popups/callPopupStore';
import { liveCallActions } from '@/features/calls/store/liveCallStore';
import { notify } from '@/features/notifications/toastApi';
import { desktopBridge } from '../bridge';
import { useDesktopEvent } from './useDesktopEvent';

/**
 * Mirrors the ringing stack onto the native call window.
 *
 * Only rings natively when the app is NOT in front: with the window focused the
 * in-app popup is already the better surface, and two ringing cards on one
 * screen is the classic desktop-client annoyance. When the window is hidden in
 * the tray — the case a browser tab simply cannot serve — the native window is
 * the only thing the user will see.
 */
export function useDesktopCalls(): void {
  const openCall = useOpenCall();

  useEffect(() => {
    const bridge = desktopBridge();
    if (bridge === null) return undefined;

    let shown: string | null = null;

    const sync = (): void => {
      const call = callPopupStore.get().calls[0] ?? null;

      if (call === null) {
        if (shown !== null) void bridge.clearCall(shown).catch(() => undefined);
        shown = null;
        return;
      }
      if (call.callId === shown) return;
      /* THE FOCUS GATE IS GONE, AND IT HAD TO GO. It skipped the native ringer
         whenever the app was in front, on the reasoning that the in-app card
         was the better surface there — and the in-app card is no longer drawn
         in the shell at all (IncomingCallHost), so keeping this would mean a
         focused user got NO ringing card whatsoever. One surface, always the
         same one, focused or not. */

      shown = call.callId;
      void bridge
        .ringCall({
          callId: call.callId,
          callerName: call.caller.displayName,
          callerRole: call.caller.role,
          kind: call.kind,
          isGroup: call.isGroup,
          otherParticipantCount: call.otherParticipantCount,
          expiresAt: call.ringExpiresAt,
        })
        .catch(() => undefined);
    };

    sync();
    return callPopupStore.subscribe(sync);
  }, []);

  useDesktopEvent('call-action', (event) => {
    if (event.action === 'accept') {
      /* Answering the NATIVE ringer opens the call in its own window, exactly
         as answering the in-app card does. The ringer is a 380x216 frameless
         popup with two buttons on it; it is not, and never was, somewhere a
         call could be held. */
      const ringing = callPopupStore.get().calls.find((call) => call.callId === event.callId);
      const peerName = ringing?.caller.displayName ?? '';
      const kind = ringing?.kind ?? 'audio';
      void callPopupActions.accept(event.callId, kind === 'video').then((callId) => {
        if (callId !== null) openCall({ callId, peerName, kind, startsConnected: true });
        else notify.error('Could not join the call. Try again.');
      });
      return;
    }
    if (event.action === 'reply' && event.text !== undefined) {
      void callPopupActions.declineWithMessage(event.callId, event.text);
      return;
    }
    void callPopupActions.decline(event.callId, 'declined');
  });

  /**
   * The call window is gone — closed, hung up, or its renderer crashed.
   *
   * This is the cross-window bus `callPopupStore` says does not exist. The call
   * ran in a document this one cannot see, so nothing over here ever learned it
   * was over. The clear is defensive and idempotent: whatever the call window
   * managed to reconcile on its way out, doing it again costs nothing.
   *
   * NEITHER THE TRAY NOR PRESENCE IS TOUCHED FROM HERE, and both for the same
   * reason. They are reported PER WINDOW now, with the shell holding the
   * aggregate, so this document declaring "not in a call" on another one's
   * behalf would wipe out its OWN voice channel — and this event names the call
   * the closing window HELD, which a different, LIVE window may have taken over
   * in the meantime (a fresh window may claim a call while the old one is still
   * closing). The shell recomputes both from what is actually alive; see
   * main/callPresence.ts and main/mediaRouter.ts.
   */
  useDesktopEvent('call-window-closed', (event) => {
    liveCallActions.leave(event.callId);
  });
}
